import { useEffect, useState } from "react";
import { api, extractErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../components/ConfirmDialog";
import Sidebar from "../components/Sidebar";
import PageTopBar from "../components/PageTopBar";
import { toast } from "sonner";
import {
    Plus,
    Pencil,
    Trash2,
    Save,
    X,
    PlayCircle,
    BookOpen,
    ExternalLink,
} from "lucide-react";

/**
 * Tutorials page — text on the left, YouTube embed on the right.
 *
 * Read access: every authenticated user.
 * Write access: super_admin only (renders the +Nuevo / Editar / Eliminar
 * controls; the backend enforces the same gate).
 */
export default function Tutorials() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";
    const confirm = useConfirm();
    const [tutorials, setTutorials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null); // tutorial id or "new"
    const [draft, setDraft] = useState(blankDraft());

    const load = async () => {
        try {
            setLoading(true);
            const r = await api.get("/tutorials");
            setTutorials(r.data || []);
        } catch (e) {
            toast.error(extractErrorMessage(e, "Error cargando tutoriales"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const startCreate = () => {
        setDraft(blankDraft(tutorials.length));
        setEditingId("new");
    };
    const startEdit = (t) => {
        setDraft({
            title: t.title || "",
            description: t.description || "",
            youtube_url: t.youtube_url || "",
            order_index: t.order_index ?? 0,
        });
        setEditingId(t.id);
    };
    const cancel = () => {
        setEditingId(null);
        setDraft(blankDraft());
    };

    const submit = async () => {
        if (!draft.title.trim()) {
            toast.error("El título no puede estar vacío");
            return;
        }
        if (!draft.youtube_url.trim()) {
            toast.error("Pega el enlace de YouTube");
            return;
        }
        try {
            if (editingId === "new") {
                await api.post("/tutorials", {
                    title: draft.title.trim(),
                    description: draft.description,
                    youtube_url: draft.youtube_url.trim(),
                    order_index: Number(draft.order_index) || 0,
                });
                toast.success("Tutorial agregado");
            } else {
                await api.put(`/tutorials/${editingId}`, {
                    title: draft.title.trim(),
                    description: draft.description,
                    youtube_url: draft.youtube_url.trim(),
                    order_index: Number(draft.order_index) || 0,
                });
                toast.success("Tutorial actualizado");
            }
            cancel();
            load();
        } catch (e) {
            toast.error(extractErrorMessage(e, "Error guardando tutorial"));
        }
    };

    const remove = async (t) => {
        const ok = await confirm({
            title: `¿Eliminar "${t.title}"?`,
            description: "El tutorial dejará de mostrarse a todos los usuarios.",
            confirmLabel: "Sí, eliminar",
            cancelLabel: "Cancelar",
            tone: "destructive",
            kind: "delete",
        });
        if (!ok) return;
        try {
            await api.delete(`/tutorials/${t.id}`);
            toast.success("Tutorial eliminado");
            load();
        } catch (e) {
            toast.error(extractErrorMessage(e, "Error eliminando"));
        }
    };

    return (
        <div className="flex min-h-screen bg-ink-950">
            <Sidebar />
            <main className="flex-1 flex flex-col">
                <PageTopBar />
                <div className="flex-1 overflow-y-auto">
                    <div className="max-w-6xl mx-auto px-6 py-10 space-y-8">
                        <header className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="min-w-0">
                                <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-2">
                                    Aprende a usar la plataforma
                                </p>
                                <h1 className="font-display text-4xl sm:text-5xl text-fog-100 leading-tight">
                                    Tutoriales
                                </h1>
                                <p className="text-fog-400 mt-3 max-w-2xl text-sm leading-relaxed">
                                    Guías cortas para sacar el máximo partido al
                                    chat, los carruseles, las referencias y los
                                    redimensionados.
                                </p>
                            </div>
                            {isSuper && (
                                <button
                                    type="button"
                                    data-testid="tutorials-new-btn"
                                    onClick={startCreate}
                                    className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-400 text-ink-on px-5 py-2.5 text-sm font-medium flex items-center gap-2 shadow-glow-sm"
                                >
                                    <Plus className="w-4 h-4" />
                                    Nuevo tutorial
                                </button>
                            )}
                        </header>

                        {editingId && (
                            <TutorialForm
                                draft={draft}
                                setDraft={setDraft}
                                onCancel={cancel}
                                onSubmit={submit}
                                isCreating={editingId === "new"}
                            />
                        )}

                        {loading ? (
                            <div className="text-fog-400 text-sm font-mono">
                                Cargando tutoriales…
                            </div>
                        ) : tutorials.length === 0 ? (
                            <EmptyState
                                isSuper={isSuper}
                                onCreate={startCreate}
                            />
                        ) : (
                            <div className="space-y-6">
                                {tutorials.map((t) => (
                                    <TutorialCard
                                        key={t.id}
                                        tutorial={t}
                                        isSuper={isSuper}
                                        onEdit={() => startEdit(t)}
                                        onDelete={() => remove(t)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}

function blankDraft(orderHint = 0) {
    return {
        title: "",
        description: "",
        youtube_url: "",
        order_index: orderHint,
    };
}

function EmptyState({ isSuper, onCreate }) {
    return (
        <div
            data-testid="tutorials-empty"
            className="rounded-2xl border hairline border-ink-600 bg-ink-900/30 p-10 text-center"
        >
            <div className="w-14 h-14 rounded-2xl bg-periwinkle-500/10 border hairline border-periwinkle-500/40 mx-auto mb-4 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-periwinkle-800" />
            </div>
            <h2 className="font-display text-2xl text-fog-100">
                Aún no hay tutoriales
            </h2>
            <p className="text-fog-400 text-sm mt-2 max-w-md mx-auto">
                {isSuper
                    ? "Sé el primero en cargar uno. Pega el enlace de YouTube y describe el flujo paso a paso."
                    : "Pronto el equipo cargará guías para que aprendas a generar carruseles, redimensionar piezas y más."}
            </p>
            {isSuper && (
                <button
                    type="button"
                    onClick={onCreate}
                    className="mt-6 rounded-xl bg-periwinkle-500 hover:bg-periwinkle-400 text-ink-on px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" /> Crear el primer tutorial
                </button>
            )}
        </div>
    );
}

function TutorialCard({ tutorial, isSuper, onEdit, onDelete }) {
    const vid = tutorial.youtube_video_id;
    const embedUrl = vid ? `https://www.youtube.com/embed/${vid}` : null;
    return (
        <article
            data-testid={`tutorial-card-${tutorial.id}`}
            className="rounded-2xl border hairline border-ink-600 bg-ink-900/40 overflow-hidden"
        >
            <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* TEXT */}
                <div className="p-6 lg:p-7 flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="font-mono text-[9px] tracking-[0.3em] text-periwinkle-800 uppercase mb-2 flex items-center gap-1.5">
                                <PlayCircle className="w-3 h-3" /> Tutorial
                                {tutorial.order_index ? (
                                    <span className="text-fog-500">
                                        · #{tutorial.order_index}
                                    </span>
                                ) : null}
                            </p>
                            <h3
                                data-testid={`tutorial-title-${tutorial.id}`}
                                className="font-display text-2xl text-fog-100 leading-snug"
                            >
                                {tutorial.title}
                            </h3>
                        </div>
                        {isSuper && (
                            <div className="flex gap-1 shrink-0">
                                <button
                                    type="button"
                                    data-testid={`tutorial-edit-${tutorial.id}`}
                                    onClick={onEdit}
                                    className="w-8 h-8 rounded-lg border hairline border-ink-600 text-fog-400 hover:text-periwinkle-800 hover:border-periwinkle-500 flex items-center justify-center"
                                    title="Editar"
                                >
                                    <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    type="button"
                                    data-testid={`tutorial-delete-${tutorial.id}`}
                                    onClick={onDelete}
                                    className="w-8 h-8 rounded-lg border hairline border-ink-600 text-fog-400 hover:text-destructive hover:border-destructive flex items-center justify-center"
                                    title="Eliminar"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                    </div>
                    <div
                        data-testid={`tutorial-body-${tutorial.id}`}
                        className="prose-tutorial text-fog-200 text-sm leading-relaxed mt-1"
                    >
                        <MarkdownLite text={tutorial.description} />
                    </div>
                    {tutorial.youtube_url && (
                        <a
                            href={tutorial.youtube_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-fog-400 hover:text-periwinkle-800 mt-2 self-start"
                        >
                            <ExternalLink className="w-3 h-3" />
                            Ver en YouTube
                        </a>
                    )}
                </div>
                {/* VIDEO */}
                <div className="bg-ink-950 flex items-center justify-center min-h-[260px] lg:min-h-[340px] border-t lg:border-t-0 lg:border-l hairline border-ink-600">
                    {embedUrl ? (
                        <iframe
                            data-testid={`tutorial-iframe-${tutorial.id}`}
                            src={embedUrl}
                            title={tutorial.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full aspect-video"
                        />
                    ) : (
                        <p className="text-fog-500 text-xs font-mono">
                            Video no disponible
                        </p>
                    )}
                </div>
            </div>
        </article>
    );
}

function TutorialForm({ draft, setDraft, onCancel, onSubmit, isCreating }) {
    return (
        <div
            data-testid="tutorial-form"
            className="rounded-2xl border hairline border-periwinkle-500/40 bg-periwinkle-500/5 p-6 space-y-4"
        >
            <div className="flex items-center justify-between gap-3">
                <h2 className="font-display text-xl text-fog-100">
                    {isCreating ? "Nuevo tutorial" : "Editar tutorial"}
                </h2>
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-fog-400 hover:text-fog-100"
                    aria-label="Cerrar form"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <FormField label="Título" className="lg:col-span-2">
                    <input
                        data-testid="tutorial-form-title"
                        value={draft.title}
                        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        placeholder="Ej: Cómo crear un carrusel"
                        className="w-full rounded-xl bg-ink-900 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm"
                    />
                </FormField>
                <FormField label="Orden (menor primero)">
                    <input
                        data-testid="tutorial-form-order"
                        type="number"
                        min="0"
                        max="10000"
                        value={draft.order_index}
                        onChange={(e) =>
                            setDraft({ ...draft, order_index: e.target.value })
                        }
                        className="w-full rounded-xl bg-ink-900 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm tabular-nums"
                    />
                </FormField>
            </div>
            <FormField label="URL de YouTube">
                <input
                    data-testid="tutorial-form-youtube"
                    value={draft.youtube_url}
                    onChange={(e) =>
                        setDraft({ ...draft, youtube_url: e.target.value })
                    }
                    placeholder="https://www.youtube.com/watch?v=…"
                    className="w-full rounded-xl bg-ink-900 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm font-mono"
                />
            </FormField>
            <FormField label="Descripción (markdown ligero · ** ** para negrita, * para bullets, [texto](url) para enlaces)">
                <textarea
                    data-testid="tutorial-form-description"
                    rows={8}
                    value={draft.description}
                    onChange={(e) =>
                        setDraft({ ...draft, description: e.target.value })
                    }
                    placeholder="Explica el paso a paso: qué hacer, dónde clickear, qué esperar…"
                    className="w-full rounded-xl bg-ink-900 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm leading-relaxed font-mono"
                />
            </FormField>
            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="rounded-xl border hairline border-ink-600 text-fog-300 hover:text-fog-100 px-5 py-2.5 text-sm"
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    data-testid="tutorial-form-save"
                    onClick={onSubmit}
                    className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-400 text-ink-on px-5 py-2.5 text-sm font-medium flex items-center gap-2"
                >
                    <Save className="w-4 h-4" />
                    {isCreating ? "Crear tutorial" : "Guardar cambios"}
                </button>
            </div>
        </div>
    );
}

function FormField({ label, children, className = "" }) {
    return (
        <div className={className}>
            <label className="font-mono text-[10px] tracking-[0.25em] text-fog-400 uppercase block mb-1.5">
                {label}
            </label>
            {children}
        </div>
    );
}

/**
 * Markdown-lite renderer.
 *
 * Supports a deliberately small subset so we don't need to pull react-markdown
 * (200+ kB) into the bundle for what is essentially help copy:
 *
 *  - blank line → new paragraph
 *  - line starting with `* ` or `- ` → bulleted list
 *  - line starting with `1. ` `2. ` … → ordered list
 *  - **bold**, *italic*, `code`
 *  - [label](url) → external link
 *
 * Everything else is rendered as plain text. HTML in the source is escaped via
 * React's default text rendering — no `dangerouslySetInnerHTML`, so users can
 * paste arbitrary content without XSS risk.
 */
function MarkdownLite({ text }) {
    if (!text) return <p className="text-fog-500 italic">Sin descripción.</p>;
    const lines = String(text).split(/\r?\n/);
    const blocks = [];
    let buffer = null; // { type: 'ul'|'ol'|'p', lines: [] }
    const flush = () => {
        if (!buffer) return;
        blocks.push(buffer);
        buffer = null;
    };
    for (const raw of lines) {
        const line = raw.trimEnd();
        if (!line.trim()) {
            flush();
            continue;
        }
        const ulMatch = /^\s*[*-]\s+(.+)$/.exec(line);
        const olMatch = /^\s*\d+\.\s+(.+)$/.exec(line);
        if (ulMatch) {
            if (!buffer || buffer.type !== "ul") {
                flush();
                buffer = { type: "ul", lines: [] };
            }
            buffer.lines.push(ulMatch[1]);
        } else if (olMatch) {
            if (!buffer || buffer.type !== "ol") {
                flush();
                buffer = { type: "ol", lines: [] };
            }
            buffer.lines.push(olMatch[1]);
        } else {
            if (!buffer || buffer.type !== "p") {
                flush();
                buffer = { type: "p", lines: [] };
            }
            buffer.lines.push(line);
        }
    }
    flush();
    return (
        <div className="space-y-3">
            {blocks.map((b, idx) => {
                if (b.type === "ul") {
                    return (
                        <ul key={idx} className="list-disc pl-5 space-y-1">
                            {b.lines.map((l, i) => (
                                <li key={i}>{inline(l)}</li>
                            ))}
                        </ul>
                    );
                }
                if (b.type === "ol") {
                    return (
                        <ol key={idx} className="list-decimal pl-5 space-y-1">
                            {b.lines.map((l, i) => (
                                <li key={i}>{inline(l)}</li>
                            ))}
                        </ol>
                    );
                }
                return (
                    <p key={idx} className="whitespace-pre-wrap">
                        {b.lines.map((l, i) => (
                            <span key={i}>
                                {i > 0 && <br />}
                                {inline(l)}
                            </span>
                        ))}
                    </p>
                );
            })}
        </div>
    );
}

// Inline tokenizer — handles bold, italic, code, and links in a single pass.
// Returns an array of React nodes safe to render directly.
function inline(text) {
    // Order matters: links first (to consume url chars), then bold (** **),
    // italic (* *), then code (` `).
    const tokens = [];
    let i = 0;
    const push = (n) => tokens.push(n);
    while (i < text.length) {
        // Link [label](url)
        if (text[i] === "[") {
            const close = text.indexOf("]", i + 1);
            if (close > i && text[close + 1] === "(") {
                const urlEnd = text.indexOf(")", close + 2);
                if (urlEnd > close + 1) {
                    const label = text.slice(i + 1, close);
                    const url = text.slice(close + 2, urlEnd);
                    if (/^https?:\/\//i.test(url)) {
                        push(
                            <a
                                key={i}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-periwinkle-800 underline hover:text-periwinkle-800"
                            >
                                {label}
                            </a>,
                        );
                        i = urlEnd + 1;
                        continue;
                    }
                }
            }
        }
        // Bold **x**
        if (text[i] === "*" && text[i + 1] === "*") {
            const end = text.indexOf("**", i + 2);
            if (end > i + 2) {
                push(
                    <strong key={i} className="text-fog-100 font-semibold">
                        {text.slice(i + 2, end)}
                    </strong>,
                );
                i = end + 2;
                continue;
            }
        }
        // Italic *x* (single)
        if (text[i] === "*" && text[i + 1] !== "*") {
            const end = text.indexOf("*", i + 1);
            if (end > i + 1) {
                push(
                    <em key={i} className="text-fog-100">
                        {text.slice(i + 1, end)}
                    </em>,
                );
                i = end + 1;
                continue;
            }
        }
        // Code `x`
        if (text[i] === "`") {
            const end = text.indexOf("`", i + 1);
            if (end > i + 1) {
                push(
                    <code
                        key={i}
                        className="bg-ink-900 px-1.5 py-0.5 rounded text-periwinkle-800 font-mono text-[12px]"
                    >
                        {text.slice(i + 1, end)}
                    </code>,
                );
                i = end + 1;
                continue;
            }
        }
        // Plain char — accumulate until next special char or end.
        let j = i;
        while (
            j < text.length &&
            text[j] !== "*" &&
            text[j] !== "`" &&
            text[j] !== "["
        ) {
            j += 1;
        }
        push(<span key={i}>{text.slice(i, j)}</span>);
        i = j;
    }
    return tokens;
}
